import React, { ChangeEvent, useEffect, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { Button, TextField } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { openDialog } from "state/slices/DialogSlice";
import { useTypedDispatch, useTypedSelector } from "hooks/store";
import { styled } from "@mui/material/styles";
import { GraphicService } from "Classes/GraphicService";
import { useCmrAuth } from "state/CMRAuth";
import { useHistory } from "react-router-dom";
import RoutesName from "routes/routesName";
import { openNotification } from "state/slices/NotificationSlice";
import CategorySelect from "ui/components/category/CategorySelect";
import CountrySelect from "../countrySelect/countrySelect";
import "./DialogSaveModel.scss";

import { SceneExportService } from "Classes/SceneExportService";

const SaveRoomText = styled(TextField)({
    "label + &": {
        color: "#e6117b",
    },
    "& label.Mui-focused": {
        color: "#e6117b",
    },
    ".MuiInputBase-input": {
        color: "#155E86",
        background: "#fff",
        border: "1px solid #ddd",
    },
    "& .MuiInput-underline:after": {
        borderBottomColor: "#e6117b",
    },
    "& .MuiFilledInput-root": {
        marginBottom: "1rem",

        "&:after": {
            borderBottomColor: "#e6117b",
        },
        "& fieldset": {
            borderColor: "#e6117b",
        },
        "&:hover fieldset": {
            borderColor: "#e6117b",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#e6117b",
        },
    },
});

const DialogSaveSceneJsonContent = (): JSX.Element => {
    const dispatch = useTypedDispatch();
    const type = useTypedSelector((store) => store.Dialog.type);
    const user = useTypedSelector((store) => store.CMRAuth.user);
    const sceneKey = useTypedSelector((store) => store.Scene.currentSceneKey);
    const currentSceneName = useTypedSelector((store) => store.Scene.sceneName);
    const sceneConfig = useTypedSelector((store) => store.Scene.sceneConfig);

    const cmrAuth = useCmrAuth();
    const router = useHistory();

    const authenticated = cmrAuth.user;
    const groups = authenticated?.groups ?? [];

    //@todo update this when we know the permissions
    const isAdmin = groups.includes("partner.admin.gbr");

    const [sceneName, setSceneName] = useState<string>("");
    const [surgeon, setSurgeon] = useState<string>("");
    const [hospital, setHospital] = useState<string>("");
    const [createdBy, setCreatedBy] = useState<string>("");
    const [isGlobal, setIsGlobal] = useState<boolean>(false);
    const [isShared, setIsShared] = useState<boolean>(false);
    const [isOwner, setIsOwner] = useState<boolean>(false);
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string | undefined>(undefined);
    const [saved, setSaved] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<number | "">("");
    const [image, setImage] = useState<string>();

    const getCategories = async () => {
        try {
            const token = await cmrAuth.getAccessToken();

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await res.json();
            setCategories(data?.categories ?? []);
        } catch (err) {
            setCategories([]);
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, boolean> = {};

        newErrors.sceneName = !sceneName || sceneName.trim().length === 0;

        if (saved === "community" || saved === "preset") {
            newErrors.surgeon = !surgeon || surgeon.trim().length === 0;

            if (saved === "community") {
                newErrors.createdBy = !createdBy || createdBy.trim().length === 0;
            }
        }

        setErrors(newErrors);

        return !Object.values(newErrors).some(Boolean);
    };

    useEffect(() => {
        getCategories();
        setSceneName(currentSceneName ?? "");
        setCreatedBy(sceneConfig?.displayName ?? user?.name ?? "");
        setIsGlobal(sceneConfig?.global);
        setIsShared(sceneConfig?.shared);
        setIsOwner(sceneConfig?.isOwner);
        setSelectedCategory(sceneConfig?.categoryId ?? "");
        setSurgeon(sceneConfig?.surgeon ?? "");
        setHospital(sceneConfig?.hospital ?? "");
        setSelectedCountry(sceneConfig?.country ?? "");

        // Preview image (still useful for user confirmation)
        const imageData = GraphicService.renderer.domElement.toDataURL();
        setImage(imageData);
    }, [currentSceneName]);

    const handleDialogClose = (): void => {
        dispatch(openDialog({ isDialogOpen: false, type }));
    };

    const handleUpdateSceneName = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setSceneName(value);

        setErrors((prev) => ({
            ...prev,
            sceneName: !value || value.trim().length === 0,
        }));
    };

    const handleUpdateSurgeon = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSurgeon(value);

        setErrors((prev) => ({
            ...prev,
            surgeon: !value || value.trim().length === 0,
        }));
    };

    const handleUpdateHospital = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setHospital(value);

        setErrors((prev) => ({
            ...prev,
            hospital: !value || value.trim().length === 0,
        }));
    };

    const handleUpdateCreatedBy = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setCreatedBy(value);

        setErrors((prev) => ({
            ...prev,
            createdBy: !value || value.trim().length === 0,
        }));
    };

    const exportToJson = (): void => {
        try {
            SceneExportService.saveCurrentScene(sceneName, true);

            dispatch(openDialog({ isDialogOpen: false, type }));

            dispatch(
                openNotification({
                    isNotificationOpen: true,
                    type: "success",
                    text: "Setup Exported.",
                })
            );
        } catch (error) {
            dispatch(
                openNotification({
                    isNotificationOpen: true,
                    type: "success",
                    text: "There was a problem exporting the setup.",
                })
            );
        }
    };

    const handleSetSaved = (value: string): void => {
        setSaved(value);
    };

    const saveToDatabase = async () => {
        const json = SceneExportService.saveCurrentScene(sceneName);

        const blob = await GraphicService.captureSceneBlob(3);

        const token = await cmrAuth.getAccessToken();

        const formData = new FormData();

        if (sceneKey) {
            formData.append("id", sceneKey);
        }

        formData.append("sceneName", sceneName);
        formData.append("data", JSON.stringify(json));
        if (saved !== "preset") {
            formData.append("displayName", String(createdBy));
        }

        if (saved === "preset") {
            formData.append("isGlobal", "true");
            formData.append("categoryId", String(selectedCategory));
            formData.append("displayName", "CMR");
        }

        if (saved === "community") {
            formData.append("isShared", "true");
            formData.append("categoryId", String(selectedCategory));
            formData.append("surgeon", String(surgeon));
            formData.append("hospital", String(hospital));
            formData.append("country", String(selectedCountry));
        }

        if (blob) {
            formData.append("thumbnail", blob);
        }

        try {
            const url =
                saved === "preset"
                    ? `${process.env.REACT_APP_API_URL}/api/global`
                    : `${process.env.REACT_APP_API_URL}/api/scene`;

            const res = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            dispatch(openDialog({ isDialogOpen: false, type }));

            const scene = await res.json();

            if (!scene.ok) {
                dispatch(
                    openNotification({
                        isNotificationOpen: true,
                        type: "error",
                        text: "There was a problem saving the setup.",
                    })
                );
                return;
            }

            dispatch(
                openNotification({
                    isNotificationOpen: true,
                    type: "success",
                    text: "Setup saved successfully.",
                })
            );

            if (scene.ok && scene.cloned) {
                router.push({
                    pathname: RoutesName.modelPage,
                    state: { sceneKey: scene.id },
                });
            }
        } catch (err) {
            dispatch(
                openNotification({
                    isNotificationOpen: true,
                    type: "error",
                    text: "There was an problem saving the setup.",
                })
            );
        }
    };

    const handleSaveScene = async (): Promise<void> => {
        const isValid = validate();

        if (!isValid) return;

        if (saved === "export") {
            exportToJson();
            return;
        }

        await saveToDatabase();
    };

    return (
        <>
            <DialogContent>
                <div className="dialogImageContainer" id="dialogImageContainer">
                    <img className="image" src={image} />
                </div>
            </DialogContent>

            <DialogActions className="cmrDialogActionContainer">
                <div style={{ width: "100%" }}>
                    <div className="saveSelection">
                        <label className={saved === "save" ? "selected" : ""}>
                            <div>
                                <input
                                    type="radio"
                                    name="saveType"
                                    onChange={() => handleSetSaved("save")}
                                />
                                <h3>
                                    {isGlobal || (isShared && !isOwner)
                                        ? "Copy Setup"
                                        : "Save Setup"}
                                </h3>
                            </div>
                            <p>
                                {isGlobal || (isShared && !isOwner)
                                    ? "Save a copy of this setup"
                                    : "Save setup to your account"}
                            </p>
                        </label>

                        {!isGlobal && (
                            <label className={saved === "community" ? "selected" : ""}>
                                <div>
                                    <input
                                        type="radio"
                                        name="saveType"
                                        onChange={() => handleSetSaved("community")}
                                    />
                                    <h3>Community Setup</h3>
                                </div>
                                <p>Allow other users to view your setup</p>
                            </label>
                        )}
                        <label className={saved === "export" ? "selected" : ""}>
                            <div>
                                <input
                                    type="radio"
                                    name="saveType"
                                    onChange={() => handleSetSaved("export")}
                                />
                                <h3>Export Setup</h3>
                            </div>
                            <p>Download setup configuration</p>
                        </label>
                        {isAdmin && (
                            <label className={saved === "preset" ? "selected" : ""}>
                                <div>
                                    <input
                                        type="radio"
                                        name="saveType"
                                        onChange={() => handleSetSaved("preset")}
                                    />
                                    <h3>Preset Setup</h3>
                                </div>
                                <p>Save as a preset</p>
                            </label>
                        )}
                    </div>

                    {saved && (
                        <section className="saveRoomName">
                            <SaveRoomText
                                error={!!errors.sceneName}
                                value={sceneName}
                                label="Procedure *"
                                onChange={handleUpdateSceneName}
                                helperText={errors.sceneName ? "Procedure name is required." : ""}
                                type="text"
                                fullWidth
                                variant="filled"
                            />

                            {saved !== "save" && saved !== "export" && (
                                <>
                                    <div style={{ marginBottom: "1rem" }}>
                                        <CategorySelect
                                            categories={categories}
                                            value={selectedCategory}
                                            onChange={setSelectedCategory}
                                        />
                                    </div>

                                    {saved === "community" && (
                                        <>
                                            <SaveRoomText
                                                error={!!errors.surgeon}
                                                value={surgeon}
                                                label="Surgeon *"
                                                onChange={handleUpdateSurgeon}
                                                helperText={
                                                    errors.surgeon
                                                        ? "Surgeon name is required."
                                                        : ""
                                                }
                                                type="text"
                                                fullWidth
                                                variant="filled"
                                            />

                                            <SaveRoomText
                                                value={hospital}
                                                label="Hospital"
                                                onChange={handleUpdateHospital}
                                                type="text"
                                                fullWidth
                                                variant="filled"
                                            />

                                            <div style={{ marginBottom: "1rem" }}>
                                                <CountrySelect
                                                    value={selectedCountry}
                                                    onChange={setSelectedCountry}
                                                />
                                            </div>

                                            <SaveRoomText
                                                error={!!errors.createdBy}
                                                value={createdBy}
                                                label="Created by *"
                                                onChange={handleUpdateCreatedBy}
                                                helperText={
                                                    errors.createdBy
                                                        ? "Created by is required."
                                                        : ""
                                                }
                                                type="text"
                                                fullWidth
                                                variant="filled"
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </section>
                    )}

                    <section className="action">
                        <Button
                            onClick={handleSaveScene}
                            fullWidth
                            disabled={!saved}
                            startIcon={<CheckOutlinedIcon />}
                            className="btn pinkRoundedButton"
                        >
                            {saved === "export" ? "Export" : "Save"}
                        </Button>

                        <Button
                            startIcon={<CloseOutlinedIcon />}
                            fullWidth
                            onClick={handleDialogClose}
                            className="btn grayRoundedButton"
                        >
                            Cancel
                        </Button>
                    </section>
                </div>
            </DialogActions>
        </>
    );
};

export default DialogSaveSceneJsonContent;
